import Immutable from 'immutable';
import installDevTools from 'immutable-devtools';
import log from 'loglevel';
import bowser from 'bowser';
import Modernizr from 'modernizr';

import { endAlert } from 'phoenix2/state/alert/actions';
import {
    isAlertShowing,
    isVideoAlertShowing,
    isVideoAlertSkippable,
} from 'phoenix2/state/alert/selectors';
import { getWaitVisible } from 'phoenix2/state/wait/selectors';
import { requestPopView } from 'phoenix2/state/views/actions';
import { userActive } from 'phoenix2/state/user/actions';
import {
    analyticsHit,
    setPageVisibility,
    initialiseContinuousKpi,
    kpiNames,
    completeRunOnceKpi,
} from 'phoenix2/state/analytics/actions';
import { triggerFeedbackHotjarIfEnabled } from 'phoenix2/utils/hotjar';

import { setBrowserDetails } from 'phoenix2/state/browser-details/actions';

import { initDragDropTouch } from 'phoenix2/utils/lib/DragDropTouch';
import { isImageZoomShowing } from 'phoenix2/state/appstate/selectors';
import { setImageZoom } from 'phoenix2/state/appstate/actions';
import { isWACShowing } from 'phoenix2/state/wac/selectors';
import { setFeatureFlags } from 'phoenix2/state/feature-flags/actions';
import { FEATURE_FLAG_A11Y_SELECTABLE_TEXT } from 'phoenix2/state/feature-flags';

const ACTIVITY_INTERVAL = 5 * 60; // seconds

/**
 * Common initialisation code for all WebApps
 *  - ensures backspace works correctly
 *  - adds support for touchscreen tap events
 *  - adds the polyfill for using HTML5 drag and drop on touch devices
 *  - adds debugging functionality
 *  - loads Katex web fonts
 * @param store - redux store needed to dispatch actions
 */
export function initApp(
    store,
    config = {
        useDnD: true,
    },
) {
    initBackspaceAndActivityRecorder(store);
    if (config.useDnD) {
        initDragDropTouch();
    }
    _initDebugging();
    _recordAppLoad(store);
    _recordIfReload(store);
    _initSupportedBrowsers(store);
    initFeatureFlags(store);
    _initPageVisibility(store);
    _initContinuousKpis(store);
    initHotjarFeedback();
    initAccessibilitySettings();
}

/**
 * Checks if hotjar feedback widget should be enabled from the feature flag and
 * show the feedback widget if it is enabled.
 */
function initHotjarFeedback() {
    triggerFeedbackHotjarIfEnabled(settings, window, 'sw-show-feedback');
}

class ActivityRecorder {
    constructor(onActivity, interval = 10000) {
        this.interval = interval;
        this.onActivity = onActivity;
    }

    release() {
        if (this.timeoutID) {
            window.clearTimeout(this.timeoutID);
        }
    }

    recordActivity() {
        if (!this.timeoutID) {
            this.onActivity(Date.now());
            this.timeoutID = window.setTimeout(() => (this.timeoutID = null), this.interval);
        }
    }
}

// Get a valid instance of Modernizr.
// This could be an imported Modernizr, or could be a version that exists on the window.
function getModernizr() {
    const globalModernizr = Modernizr;
    // If the global version exists but isn't properly instantiated, it will be an empty object {}.
    // {} is truthy so returning `Modernizr || window.Modernizr`, gives the empty object rather than
    // the valid version on the window.
    // Hence we do a slightly better check on Modernizr, ensuring it isn't an empty object.
    const globalIsEmpty =
        !globalModernizr || (globalModernizr && Object.keys(globalModernizr).length === 0);
    if (!globalIsEmpty) {
        return globalModernizr;
    }
    return window.Modernizr || undefined;
}

/**
 * Prevents backspace from going back in some browsers and the
 * activity recorder which sends USER_ACTIVE actions at regular
 * intervals while the user is active.
 */
export function initBackspaceAndActivityRecorder(store) {
    let backspaceIsPressed = false;

    const recorder = new ActivityRecorder(
        ts => store.dispatch(userActive(ts)),
        ACTIVITY_INTERVAL * 1000,
    );
    const recordActivity = recorder.recordActivity.bind(recorder);

    document.addEventListener('keydown', event => {
        recordActivity();
        if (event.which === 8) {
            backspaceIsPressed = true;
        }
    });

    document.addEventListener('keyup', event => {
        if (event.which === 8) {
            backspaceIsPressed = false;
        }
    });

    document.addEventListener('touchstart', recordActivity);
    document.addEventListener('mousemove', recordActivity);

    const modernizr = getModernizr();

    if (modernizr.history) {
        history.pushState(null, '', location.href);
    }

    // After the app has loaded, add a listener for browser back being pressed
    // We have to wait until the app has loaded because older browsers (e.g. IOS
    // Safari before v10) fire a popstate event during the initial page load
    window.addEventListener(
        'load',
        function() {
            setTimeout(function() {
                window.onpopstate = event => {
                    if (window.location.href.search('#/student') !== -1) {
                        return;
                    }
                    if (modernizr.history) {
                        history.pushState(null, '', location.href);
                    }
                    const state = store.getState();
                    if (isVideoAlertShowing(state) && isVideoAlertSkippable(state)) {
                        store.dispatch(endAlert('close'));
                    } else if (
                        backspaceIsPressed ||
                        isAlertShowing(state) ||
                        isWACShowing(state) ||
                        getWaitVisible(state)
                    ) {
                        return;
                    } else if (isImageZoomShowing(state)) {
                        store.dispatch(setImageZoom(null));
                    } else {
                        store.dispatch(requestPopView());
                    }
                };
            }, 0);
        },
        false,
    );
}

/**
 * Set properties on window for debugging.
 */
function _initDebugging() {
    window.$I = Immutable;
    installDevTools(Immutable);
    window.log = log;
}

/**
 * Creates a string giving human-readable details of the browser and os, for
 * use in reporting errors - e.g. "Chrome 71.0 on macOS 10.14.0"
 * @param bowser
 * @returns {string}
 */
const getBrowserDetails = bowser => {
    const browser = bowser.name || 'unknown';
    const browserVersion = bowser.version || '';
    const os = bowser.osname || 'unknown';
    const osVersion = bowser.osversion || '';

    return {
        browser,
        browserVersion,
        os,
        osVersion,
        label:
            browser +
            (browserVersion ? '/' + browserVersion : '') +
            ' on ' +
            os +
            (osVersion ? '/' + osVersion : ''),
    };
};

export function _initSupportedBrowsers(store) {
    const modernizr = getModernizr();

    // Stop if either of our feature detection libraries aren't working.
    if (!bowser || !modernizr) {
        log.warn('Browser Detection failed.', 'Bowser:', bowser, 'Modernizr:', modernizr);
        return;
    }

    let oldVersion = false;
    let missingFeature = false;
    let animationBroken = false;

    // Browsers known to be unsupported.
    if (bowser.msie) {
        oldVersion = true;
        animationBroken = true;
    } else if (bowser.safari && bowser.version < 10) {
        oldVersion = true;
    }

    // Use Modernizr to detect unsupported features.
    const detectFeature = (feature, isPresent) => {
        if (!isPresent) {
            missingFeature = true;

            // Trigger an analytics hit for each unsupported feature.
            store.dispatch(
                analyticsHit('event', {
                    eventCategory: 'UnsupportedBrowserFeature',
                    eventAction: feature,
                }),
            );
        }
    };

    detectFeature('canvas', modernizr.canvas);
    detectFeature('video', modernizr.video);
    detectFeature('history', modernizr.history);
    detectFeature('flexbox', modernizr.flexbox);
    detectFeature('flexwrap', modernizr.flexwrap);
    detectFeature('fetch', modernizr.fetch);

    // Work out if this is a desktop or mobile browser.
    let platform = 'desktop';
    if (bowser.android) {
        platform = 'android';
    } else if (bowser.ios) {
        platform = 'ios';
    }

    store.dispatch(
        setBrowserDetails({
            platform,
            oldVersion,
            missingFeature,
            supportsFetch: !!modernizr.fetch,
            animationBroken,
            browserIsInternetExplorer: !!bowser.msie,
            browserDetails: getBrowserDetails(bowser),
        }),
    );
}

/**
 * Record any feature flags in the global settings.flag in the state
 * @param store
 * @private
 */
export function initFeatureFlags(store) {
    store.dispatch(setFeatureFlags(settings && settings.flags));
}

/**
 * Fire analytics events when the page visibility changes - i.e. user goes to
 * a different browser tab, minimises the app, or locks their device
 * @param store the Redux store
 * @private
 */
function _initPageVisibility(store) {
    let hidden = false;

    // Find out when the page visibility changes, and fire an event
    const visibilityListener = () => {
        if (hidden !== document.hidden) {
            hidden = document.hidden;
            store.dispatch(setPageVisibility(hidden));
        }
    };

    if (typeof document.hidden !== 'undefined') {
        hidden = document.hidden;
        document.addEventListener('visibilitychange', visibilityListener, false);
    }
}

function _recordAppLoad(store) {
    store.dispatch(completeRunOnceKpi(kpiNames.TIME_TO_APP_LOAD));
}

function _initContinuousKpis(store) {
    store.dispatch(initialiseContinuousKpi(kpiNames.SPINNING_WHEEL));
}

function _recordIfReload(store) {
    try {
        if (_determineIfReload()) {
            store.dispatch(completeRunOnceKpi(kpiNames.PAGE_REFRESH));
        }
    } catch (e) {
        // Catch and ignore any errors just in case
    }
}

/**
 * Determines if the currently loaded page was navigated to via reloading the page.
 * It will attempt to query both version of the navigation timing api.
 * @returns {boolean|undefined} boolean of if reloaded or undefined if cannot determine
 * @private
 */
function _determineIfReload() {
    const performance = window.performance;
    if (performance) {
        if (performance.getEntriesByType) {
            // Navigation Timing Level 2 API
            const entries = performance.getEntriesByType('navigation');
            if (entries && entries.length > 0) {
                return entries[0].type === 'reload';
            }
        }
        if (performance.navigation) {
            // Navigation Timing Level 1 API - required for Safari and older browsers
            return performance.navigation.type === performance.navigation.TYPE_RELOAD;
        }
    }
    return undefined; // Unknown
}

function initAccessibilitySettings() {
    // settings.flags doesnt exist in numerise, so dont try to access it if it doesn't
    if (settings?.flags?.[FEATURE_FLAG_A11Y_SELECTABLE_TEXT]) {
        // If the selectable text feature flag is turned on, update the accessibility CSS custom properties.
        const r = document.querySelector(':root');
        // We set user-select to default to allow text to be selected.
        r.style.setProperty('--user-select-accessibility-setting', 'default');
        // We set the cursor back to automatic rather than the 'default' arrow cursor.
        r.style.setProperty('--cursor-accessibility-setting', 'auto');
    }
}
